import React from 'react';
import { motion } from 'motion/react';
import { Shield, Target, CheckCircle2, ArrowRight, Zap, Lock, Activity, Users, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import MultiStepLeadForm from '@/src/components/MultiStepLeadForm';

export default function PenetrationTesting() {
  return (
    <div className="relative pt-32 pb-24 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,150,255,0.05),transparent_50%)]" />
        <div className="absolute top-0 left-0 w-full h-full cyber-grid opacity-10" />
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-20 items-start">
          {/* Left Side: Sales Copy */}
          <div className="space-y-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-accent text-[10px] font-bold uppercase tracking-[0.4em]"
            >
              <Target className="w-3 h-3" /> Real-World Simulation
            </motion.div>

            <div className="space-y-6">
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-5xl md:text-7xl font-display font-bold tracking-tighter leading-[0.9]"
              >
                Think Like a Hacker. <br />
                <span className="text-accent glow-text">Secure Like a Pro.</span>
              </motion.h1>
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-xl text-muted-foreground font-light max-w-xl leading-relaxed"
              >
                Automated tools only go so far. Our elite penetration testers use manual techniques to find the complex vulnerabilities that scanners miss.
              </motion.p>
            </div>

            {/* Pain Points */}
            <div className="space-y-8">
              <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">The Critical Gaps We Find:</h3>
              <div className="grid gap-4">
                {[
                  "Complex business logic vulnerabilities",
                  "Privilege escalation and lateral movement",
                  "Sensitive data exposure and exfiltration",
                  "Advanced social engineering and physical security"
                ].map((point, i) => (
                  <div key={i} className="flex items-center gap-3 text-red-400/80">
                    <Zap className="w-5 h-5 shrink-0" />
                    <span className="text-sm font-medium">{point}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10 space-y-4">
                <Shield className="w-8 h-8 text-accent" />
                <h4 className="font-bold">Hardened Defenses</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">We provide a clear roadmap to remediate vulnerabilities and strengthen your security posture.</p>
              </div>
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10 space-y-4">
                <FileText className="w-8 h-8 text-accent" />
                <h4 className="font-bold">Executive Reporting</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">Detailed reports for both technical teams and executive stakeholders, including risk scores.</p>
              </div>
            </div>

            <div className="flex items-center gap-6 p-6 rounded-2xl bg-accent/5 border border-accent/20">
              <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center text-accent">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm font-bold">Elite Testing Team</p>
                <p className="text-xs text-muted-foreground">Certified experts with decades of combined experience.</p>
              </div>
            </div>
          </div>

          {/* Right Side: Form */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="sticky top-32"
          >
            <div className="glass-card p-10 rounded-[3rem] border-white/10 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-accent/10 blur-[60px] rounded-full" />
              <div className="text-center mb-10 space-y-2 relative z-10">
                <h2 className="text-3xl font-display font-bold">Request a <span className="text-accent">Pentest</span></h2>
                <p className="text-sm text-muted-foreground font-light italic">Simulate an attack, secure your future.</p>
              </div>
              <MultiStepLeadForm />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
